import type { ReactElement, ReactNode } from 'react'
import { Box, Stack, SwipeableDrawer, Typography } from '@mui/material'

interface BottomSheetProps {
  open: boolean
  onClose: () => void
  onOpen?: () => void
  titleId: string
  title: ReactNode
  /** Right side of the title row: a reset or a close button. */
  headerAction?: ReactNode
  /** The body; it owns its scrolling (`overflowY: 'auto'`, `flex: 1`). */
  children: ReactNode
  /** Takes the whole viewport height instead of at most 90%. */
  fullHeight?: boolean
}

function noop(): void {}

/** Phone bottom sheet: handle, title row, body; at most 90% of the viewport high unless `fullHeight`. */
export default function BottomSheet({
  open,
  onClose,
  onOpen = noop,
  titleId,
  title,
  headerAction,
  children,
  fullHeight = false
}: BottomSheetProps): ReactElement {
  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      disableSwipeToOpen
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-modal': true,
          'aria-labelledby': titleId,
          sx: {
            maxHeight: fullHeight ? '100%' : '90vh',
            ...(fullHeight ? { height: '100%' } : {}),
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            display: 'flex',
            flexDirection: 'column'
          }
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.25, pb: 0.5 }}>
        <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: 'divider' }} />
      </Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, pb: 1.25, borderBottom: 1, borderColor: 'divider' }}
      >
        <Typography id={titleId} variant="h3" component="h2">
          {title}
        </Typography>
        {headerAction}
      </Stack>
      {children}
    </SwipeableDrawer>
  )
}
